import React from 'react';

// ── Inline figures ────────────────────────────────────────────────────────────

const MandatoryFieldsFigure = () => (
  <div style={{ marginTop: 16, display: 'inline-flex', flexDirection: 'column', gap: 10, background: '#1e1e2e', padding: '16px 20px', borderRadius: 8 }}>
    <div style={{ fontSize: 11, color: '#9399b2', marginBottom: 2 }}>Example: dialog with required fields</div>
    {[
      { label: 'Shipping date actual', required: true, value: '2025-06-12' },
      { label: 'Gross weight (kg)', required: true, value: '' },
      { label: 'Net weight (kg)', required: true, value: '' },
      { label: 'Notes', required: false, value: 'optional' },
    ].map(({ label, required, value }) => (
      <div key={label} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <label style={{ fontSize: 11, color: required ? '#f38ba8' : '#9399b2' }}>
          {label}{required ? <span style={{ color: '#f38ba8' }}> *</span> : null}
        </label>
        <div style={{
          background: '#313244', borderRadius: 4, padding: '5px 10px',
          border: `1.5px solid ${required && !value ? '#f38ba8' : '#45475a'}`,
          fontSize: 13, color: value ? '#cdd6f4' : '#45475a',
          minWidth: 220,
        }}>
          {value || (required ? 'Required — cannot be empty' : ' ')}
        </div>
      </div>
    ))}
  </div>
);

const ImportFormatFigure = () => {
  const headers = ['customer', 'order_ID', 'shipping_date_planned', 'quantity', 'CPN', 'invoice_due'];
  const sample  = ['Acme GmbH', 'ORD-001', '2025-08-15', '500', 'ABC-123', '2025-09-01'];
  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ fontSize: 11, color: '#9399b2', marginBottom: 6 }}>Required column headers (case-sensitive)</div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', fontSize: 12, fontFamily: "'JetBrains Mono', monospace", background: '#1e1e2e', borderRadius: 8, overflow: 'hidden' }}>
          <thead>
            <tr>
              {headers.map(h => (
                <th key={h} style={{ padding: '6px 12px', background: '#313244', color: '#89b4fa', borderBottom: '1px solid #45475a', borderRight: '1px solid #45475a', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              {sample.map((v, i) => (
                <td key={i} style={{ padding: '5px 12px', color: '#a6e3a1', borderRight: '1px solid #313244', whiteSpace: 'nowrap' }}>
                  {v}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
      <div style={{ fontSize: 11, color: '#6c7086', marginTop: 6 }}>
        Optional columns: CPN, note, status, invoice_date, shipping_date_real, invoice_due
      </div>
    </div>
  );
};

const ConsignLifecycleFigure = () => {
  const phases = [
    { label: 'Fresh order',          sub: 'e.g. 1000 units total',                          color: '#89b4fa' },
    { label: 'Generate STWXE',       sub: 'Status → stwxe',                                 color: '#89b4fa' },
    { label: 'Generate Invoice',     sub: 'Status → shipped',                               color: '#89b4fa' },
    { label: 'Consign',              sub: 'Status → consigned (master order)',               color: '#fab387' },
    { label: 'Invoice ×N on master', sub: 'Each call creates a sub-order (001-1, 001-2…)', color: '#a6e3a1' },
    { label: 'Fully consumed',       sub: 'partial_qty ≥ total_qty — no further invoices',  color: '#f38ba8' },
  ];
  return (
    <div style={{ marginTop: 16, background: '#1e1e2e', borderRadius: 8, padding: '16px 18px', maxWidth: 440, display: 'inline-block' }}>
      <div style={{ fontSize: 11, color: '#9399b2', marginBottom: 12 }}>Consignment lifecycle</div>
      {phases.map(({ label, sub, color }, i) => (
        <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 18, flexShrink: 0 }}>
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: color, marginTop: 4, flexShrink: 0 }} />
            {i < phases.length - 1 && (
              <div style={{ width: 2, height: 22, background: '#313244', margin: '3px 0' }} />
            )}
          </div>
          <div style={{ paddingBottom: i < phases.length - 1 ? 0 : 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color, lineHeight: 1.3 }}>{label}</div>
            <div style={{ fontSize: 11, color: '#6c7086', marginBottom: i < phases.length - 1 ? 10 : 0 }}>{sub}</div>
          </div>
        </div>
      ))}
    </div>
  );
};

const SubOrderFigure = () => (
  <div style={{ marginTop: 16, background: '#1e1e2e', borderRadius: 8, padding: '14px 16px', maxWidth: 500, display: 'inline-block' }}>
    <div style={{ fontSize: 11, color: '#9399b2', marginBottom: 10 }}>Example: order 001 with three partial deliveries</div>
    {[
      { id: '001',   type: 'Master', qty: '1000', partial: '—',    status: 'consigned', accent: '#fab387' },
      { id: '001-1', type: 'Sub',    qty: '200',  partial: '200',  status: 'shipped',   accent: '#89b4fa' },
      { id: '001-2', type: 'Sub',    qty: '300',  partial: '500',  status: 'shipped',   accent: '#89b4fa' },
      { id: '001-3', type: 'Sub',    qty: '500',  partial: '1000', status: 'paid',      accent: '#a6e3a1' },
    ].map(({ id, type, qty, partial, status, accent }) => (
      <div key={id} style={{ display: 'grid', gridTemplateColumns: '70px 55px 1fr 1fr 80px', gap: 1, marginBottom: 3 }}>
        {[id, type, `qty: ${qty}`, `partial: ${partial}`, status].map((cell, ci) => (
          <div key={ci} style={{
            fontSize: 12,
            fontFamily: "'JetBrains Mono', monospace",
            padding: '4px 8px',
            background: ci === 0 ? '#313244' : '#2a2a3e',
            color: ci === 0 ? accent : ci === 4 ? accent : '#cdd6f4',
            borderRadius: ci === 0 ? '4px 0 0 4px' : ci === 4 ? '0 4px 4px 0' : 0,
          }}>
            {cell}
          </div>
        ))}
      </div>
    ))}
    <div style={{ fontSize: 11, color: '#6c7086', marginTop: 8 }}>
      After 001-3: partial_qty (1000) ≥ total_qty (1000) → consignment fully consumed
    </div>
  </div>
);

// ── Order lifecycle flowchart ────────────────────────────────────────────────

export const StatusFlowchart = ({ onJumpToTour }) => {
  const Doc = ({ label, tourId }) => (
    <span
      onClick={() => tourId && onJumpToTour(tourId)}
      style={{
        display: 'inline-block', padding: '2px 10px',
        background: 'rgba(137,180,250,0.12)', border: '1px solid rgba(137,180,250,0.3)',
        color: '#89b4fa', borderRadius: 20, fontSize: 11, fontWeight: 500,
        cursor: tourId ? 'pointer' : 'default', userSelect: 'none', whiteSpace: 'nowrap',
        transition: 'background 0.12s',
      }}
      onMouseEnter={e => { if (tourId) e.currentTarget.style.background = 'rgba(137,180,250,0.25)'; }}
      onMouseLeave={e => { if (tourId) e.currentTarget.style.background = 'rgba(137,180,250,0.12)'; }}
    >
      {label}
    </span>
  );

  const StatusNode = ({ label, color }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{ width: 13, height: 13, borderRadius: '50%', background: color, flexShrink: 0, boxShadow: `0 0 8px ${color}66` }} />
      <span style={{ fontSize: 14, fontWeight: 700, color, letterSpacing: '0.05em' }}>{label}</span>
    </div>
  );

  const Connector = ({ label, tourId }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '5px 0 5px 5px' }}>
      <div style={{ width: 2, height: 26, background: '#313244', flexShrink: 0 }} />
      <span style={{ fontSize: 11, color: '#6c7086' }}>via</span>
      <Doc label={label} tourId={tourId} />
    </div>
  );

  const SideDocs = ({ items }) => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 7, marginLeft: 23, alignItems: 'center' }}>
      <span style={{ fontSize: 11, color: '#45475a' }}>optional:</span>
      {items.map(({ label, tourId }) => <Doc key={label} label={label} tourId={tourId} />)}
    </div>
  );

  return (
    <div style={{
      background: '#1e1e2e', borderRadius: 10, padding: '22px 26px',
      fontFamily: "'Inter', system-ui, sans-serif",
      display: 'inline-block', minWidth: 380, maxWidth: 520,
    }}>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#45475a', marginBottom: 20 }}>
        Order Lifecycle — click any document type to open its guide
      </div>

      <StatusNode label="FRESH" color="#89b4fa" />
      <SideDocs items={[{ label: 'Confirmation', tourId: 'confirmation' }, { label: 'Proforma', tourId: 'proforma' }]} />
      <Connector label="STWXE" tourId="stwxe-order" />

      <StatusNode label="STWXE" color="#a6e3a1" />
      <SideDocs items={[{ label: 'Shipping Note', tourId: 'shipping-note' }]} />
      <Connector label="Invoice" tourId="invoice" />

      <StatusNode label="SHIPPED" color="#f9e2af" />
      <SideDocs items={[
        { label: 'China Import', tourId: 'china-import' },
        { label: 'Credit Note', tourId: 'corrections' },
        { label: 'Combine docs', tourId: 'combine' },
      ]} />

      {/* Branch: Consign vs direct paid */}
      <div style={{ display: 'flex', gap: 20, marginTop: 10 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 6px 5px' }}>
            <div style={{ width: 2, height: 24, background: '#313244', flexShrink: 0 }} />
            <span style={{ fontSize: 11, color: '#6c7086' }}>via</span>
            <Doc label="Consign" tourId="consign" />
          </div>
          <StatusNode label="CONSIGNED" color="#fab387" />
          <div style={{ margin: '5px 0 5px 5px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 2, height: 24, background: '#313244', flexShrink: 0 }} />
            <span style={{ fontSize: 11, color: '#6c7086' }}>Invoice ×N per delivery</span>
          </div>
          <StatusNode label="PAID" color="#a6e3a1" />
        </div>

        <div style={{ width: 1, background: '#313244', alignSelf: 'stretch', flexShrink: 0 }} />

        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', paddingBottom: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <span style={{ fontSize: 11, color: '#6c7086' }}>via</span>
            <Doc label="Mark Paid" tourId="corrections" />
          </div>
          <StatusNode label="PAID" color="#a6e3a1" />
        </div>
      </div>

      <div style={{ marginTop: 18, fontSize: 11, color: '#313244', borderTop: '1px solid #313244', paddingTop: 12, color: '#45475a' }}>
        All generated files are archived in the Files page.
      </div>
    </div>
  );
};

// ── Sidebar sections ──────────────────────────────────────────────────────────

export const SECTIONS = [
  { label: 'Navigation & Search',  ids: ['search'] },
  { label: 'Order Ingest',         ids: ['new-order', 'edit-order'] },
  {
    label: 'Document Generation',
    ids: ['generate-files', 'confirmation', 'proforma', 'stwxe-order', 'shipping-note', 'invoice', 'china-import', 'consign', 'combine', 'corrections'],
  },
  { label: 'Tools & Pages', ids: ['stwxe-checker', 'notifications', 'files-templates', 'activity-log'] },
  { label: 'FAQ',           ids: ['faq'] },
];

// ── Tours ─────────────────────────────────────────────────────────────────────

export const TOURS = [

  // ── Navigation & Search ────────────────────────────────────────────────────
  {
    id: 'search',
    title: 'Search & Filters',
    steps: [
      {
        title: 'Searching orders',
        body: 'Use the {search-form|search bar} to filter the order table by any text field — customer name, order ID, CPN, or status. Press {search-button|Search} to apply, or {reset-button|Reset} to clear everything and show all orders.',
      },
      {
        title: 'Quick-filter buttons',
        body: 'The {boomer-buttons|quick-filter strip} below the search bar lets you filter by status in one click. Each button shows only orders matching that status (fresh, stwxe, shipped, consigned, paid…). Click the active button again to deselect it.',
      },
      {
        title: 'Selecting orders',
        body: 'Check the {order-select|checkbox column} to select one or more orders. Most buttons in the {action-panel|action panel} apply to the current selection — you can generate the same document for multiple orders at once.',
      },
      {
        title: 'Reordering and pinning columns',
        body: 'Drag any {column-header|column header} left or right to reorder the table. Click the {pin-icon|pin icon} that appears on hover to freeze a column to the left side — useful for keeping the order ID visible while scrolling right.',
      },
      {
        title: 'Adding and hiding columns',
        body: 'Click {add-column-button|+ Column} above the table to toggle which fields are shown. Your column layout is saved in local storage and persists between sessions.',
      },
    ],
  },

  // ── Order Ingest ───────────────────────────────────────────────────────────
  {
    id: 'new-order',
    title: 'Adding Orders',
    steps: [
      {
        title: 'Import from a spreadsheet',
        body: 'Click {import-button|Import} in the header to open a file picker. Upload a .xlsx or .csv file. Column headers must match exactly — they are case-sensitive. A log entry is created for every import attempt; check the Activity Log if rows were skipped.',
        figure: ImportFormatFigure,
      },
      {
        title: 'Minimum required columns',
        body: 'Every import must include these four columns. Any other fields are optional and can be added later via Edit.',
        code: 'customer              (text)\norder_ID              (text, unique)\nshipping_date_planned (YYYY-MM-DD)\nquantity              (integer)',
      },
      {
        title: 'Add a single order manually',
        body: 'Click {add-order-button|Add order} in the header to open a blank form. Fill in at minimum: customer, order ID, planned shipping date, and quantity. All other fields can be filled in later via the Edit button.',
      },
    ],
  },
  {
    id: 'edit-order',
    title: 'Editing Orders',
    steps: [
      {
        title: 'Opening the edit form',
        body: 'Select one order and click {edit-button|Edit}. The dialog lets you update any field — customer details, dates, quantities, CPN, notes, and invoice information.',
      },
      {
        title: 'Sub-positions',
        body: 'For orders with multiple line items (different CPNs or quantity splits), click {sub-position-button|Sub-position} to create a child order. Sub-positions inherit the parent\'s order ID with a letter suffix (e.g. 001 → 001-A, 001-B) and can have their own documents generated independently.',
      },
      {
        title: 'Inline cell editing',
        body: 'Some fields can be edited directly in the table without opening the dialog. Double-click a cell — if it\'s inline-editable, it becomes an input field. Press Enter or click outside to save.',
      },
    ],
  },

  // ── Document Generation ────────────────────────────────────────────────────
  {
    id: 'generate-files',
    title: 'Document Generation: Overview',
    steps: [
      {
        title: 'Generating a document',
        body: 'Select one or more orders from the table, then click a document button in the {action-panel|action panel} on the right. A dialog opens for any information the document type requires. After filling in the fields, click the submit button at the bottom to generate.',
      },
      {
        title: 'Required fields (*)',
        body: 'Fields marked with a red asterisk (*) must be filled before the document can be generated. Submitting with an empty required field outlines it in red and blocks submission. Scroll through the full dialog — required fields may be below the visible area.',
        figure: MandatoryFieldsFigure,
      },
      {
        title: 'The Generate button',
        body: 'After filling all required fields, click the\n{generate-submit|Generate … file}\nbutton at the bottom of the dialog. The label changes to reflect the document type (e.g. "Generate invoice file").',
      },
      {
        title: 'What happens after generation',
        body: 'The generated PDF appears in the Files page. Certain document types also change the order\'s status automatically:\n\n• Confirmation → no status change\n• STWXE → fresh → stwxe\n• Invoice → stwxe → shipped\n• Consign → shipped → consigned\n• Mark Paid → shipped / consigned → paid\n\nIf the status didn\'t change, check the Activity Log for the error.',
      },
    ],
  },
  {
    id: 'confirmation',
    title: 'Confirmation PDF',
    steps: [
      {
        title: 'What is a confirmation?',
        body: 'The confirmation document is typically the first document in an order\'s lifecycle. It acknowledges the order to the customer and locks in quantities and pricing. Generating it does not change the order status.',
      },
      {
        title: 'Generating a confirmation',
        body: 'Select one or more fresh orders and click {confirmation-button|Confirmation} in the action panel. The dialog has no additional required fields. Click {generate-submit|Generate confirmation file} to produce the PDF.',
      },
    ],
  },
  {
    id: 'proforma',
    title: 'Proforma Invoice',
    steps: [
      {
        title: 'What is a proforma invoice?',
        body: 'A proforma is a pre-shipment price confirmation. It looks like an invoice but has no effect on the order\'s status or accounting. Use it when the customer needs a price estimate or customs pre-clearance before goods ship.',
      },
      {
        title: 'Generating a proforma',
        body: 'Select an order in any status and click {proforma-button|Proforma}. Fill in price and currency if not already set. Click {generate-submit|Generate proforma file} to produce the PDF.',
      },
    ],
  },
  {
    id: 'stwxe-order',
    title: 'STWXE Order',
    steps: [
      {
        title: 'What is the STWXE document?',
        body: 'STWXE is a supplier export / handover document. Generating it confirms the order is ready for shipment and advances the status from fresh → stwxe. Without this step, Invoice generation is blocked.',
      },
      {
        title: 'Generating an STWXE',
        body: 'Select one or more orders with status fresh and click {stwxe-button|STWXE}. Fill in the shipping details in the dialog, then click {generate-submit|Generate stwxe file} to produce the PDF and advance the status.',
      },
      {
        title: 'Verify with the STWXE Checker',
        body: 'After generating, you can cross-check the invoice ID in the document against the database using the STWXE Checker page (accessible from the header). See the STWXE Checker tour for details.',
      },
    ],
  },
  {
    id: 'shipping-note',
    title: 'Shipping Note',
    steps: [
      {
        title: 'What is a shipping note?',
        body: 'The shipping note is a packing and dispatch document that accompanies the physical goods to the customer. It does not change the order status.',
      },
      {
        title: 'Generating a shipping note',
        body: 'Select one or more orders (typically in stwxe status) and click {shipping-note-button|Shipping Note}. Fill in carrier and package details, then click {generate-submit|Generate shipping note file}.',
      },
    ],
  },
  {
    id: 'invoice',
    title: 'Invoice',
    steps: [
      {
        title: 'What does an invoice do?',
        body: 'The invoice is the primary payment document. Generating it advances the order from stwxe → shipped. For consigned master orders it instead creates a new sub-order for the partial delivery. German customers (nationality: DE) also receive an XRechnung XML file alongside the PDF.',
      },
      {
        title: 'Generating an invoice',
        body: 'Select one or more orders with status stwxe and click {invoice-button|Invoice}. Fill in the invoice date and any price adjustments, then click {generate-submit|Generate invoice file}. The PDF (and XML if applicable) is saved to the Files page.',
      },
      {
        title: 'XRechnung XML for German customers',
        body: 'If the customer\'s nationality is DE, the system automatically generates an XRechnung-format XML alongside the invoice PDF. This is the legally required e-invoice format for B2G transactions in Germany. The XML appears as a separate entry on the Files page.',
      },
    ],
  },
  {
    id: 'china-import',
    title: 'China Import',
    steps: [
      {
        title: 'What is the China Import form?',
        body: 'The China Import document is a customs declaration form for shipments entering via Chinese import channels. It collects commodity HS codes, declared values, and related customs fields.',
      },
      {
        title: 'Generating a China Import form',
        body: 'Select the order and click {china-import-button|China Import}. Fill in the HS codes, declared unit value, and any special customs notes. Click {generate-submit|Generate china import file} to produce the form.',
      },
    ],
  },
  {
    id: 'consign',
    title: 'Consignment Workflow',
    steps: [
      {
        title: 'What is consignment?',
        body: 'Consignment splits a large order into multiple partial deliveries. A single "master" order tracks the full quantity; each delivery generates a child sub-order with its own invoice. The master stays in consigned status until all quantity has been delivered.',
        figure: ConsignLifecycleFigure,
      },
      {
        title: 'Step 1 — Bring the order to shipped status',
        body: 'Before consigning, the order must go through the standard flow:\n\n1. Select the order (status: fresh)\n2. Click {stwxe-button|STWXE} → status becomes stwxe\n3. Click {invoice-button|Invoice} → status becomes shipped\n\nYou cannot consign a fresh order directly.',
      },
      {
        title: 'Step 2 — Apply consignment',
        body: 'With the order in shipped status, click {consign-button|Consign}. The dialog asks for the total consignment quantity and any reference notes. Click {generate-submit|Generate consign file} to transition the order to consigned status. No PDF is produced at this step.',
      },
      {
        title: 'Step 3 — Partial deliveries via Invoice',
        body: 'Each partial delivery is triggered by clicking {invoice-button|Invoice} on the master order. A sub-order is created automatically (e.g. 001-1, 001-2…) with the quantity entered in the invoice dialog. Each sub-order starts in shipped status and can be independently marked as paid.',
        figure: SubOrderFigure,
      },
      {
        title: 'Tracking delivery progress',
        body: 'The master order\'s partial_delivered_qty field increments with each invoice. When it reaches the total quantity, the system blocks further invoices with "Consignment fully consumed." At that point, use {mark-paid-button|Mark Paid} on the master to close it out.',
      },
      {
        title: 'Completing a consignment',
        body: 'Click {mark-paid-button|Mark Paid} on the master order once fully consumed. Each sub-order can also be independently marked paid as customer payments arrive. The master transitioning to paid signals that the entire consignment is financially settled.',
      },
    ],
  },
  {
    id: 'combine',
    title: 'Combined Documents',
    steps: [
      {
        title: 'Combining invoices',
        body: 'Select two or more orders and click {combine-invoice-button|Combine Invoice} to merge them into a single invoice PDF. Useful when a customer orders multiple line items and wants one document. Both orders must be in a compatible status.',
      },
      {
        title: 'Combined STWXE and confirmation',
        body: '{combine-stwxe-button|Combine STWXE} and {combine-confirmation-button|Combine Confirmation} merge the respective documents for multiple selected orders into one PDF. The orders themselves are not merged — only the generated document contains both.',
      },
    ],
  },
  {
    id: 'corrections',
    title: 'Corrections & Cancellations',
    steps: [
      {
        title: 'Credit note',
        body: 'A credit note reverses or partially reverses an issued invoice. Select the order and click {credit-note-button|Credit Note}. Fill in the credit amount and reason. The PDF is archived in Files; the order status does not change automatically.',
      },
      {
        title: 'Cancel invoice',
        body: 'Click {cancel-invoice-button|Cancel Invoice} to void the most recent invoice PDF and mark it as cancelled in the system. This does not revert the order status automatically — use {edit-button|Edit} if you need to re-generate from an earlier status.',
      },
      {
        title: 'Cancel order',
        body: 'Click {cancel-order-button|Cancel Order} to soft-delete the entire order. It remains visible with a "cancelled" status. Use the {boomer-buttons|quick-filter strip} to hide cancelled orders from the default view.',
      },
    ],
  },

  // ── Tools & Pages ──────────────────────────────────────────────────────────
  {
    id: 'stwxe-checker',
    title: 'STWXE Checker',
    steps: [
      {
        title: 'What does the STWXE Checker do?',
        body: 'The STWXE Checker lets you paste an invoice ID and verify that it exists in the database. Use it to cross-reference physical shipment documents against system records — useful during audits or when a carrier asks to verify an invoice number.',
      },
      {
        title: 'Using the checker',
        body: 'Navigate to the STWXE Checker via the header menu. Enter the invoice ID in the text field and press Check. A green result means the ID was found; red means it\'s not in the database — either it was never imported, or the ID contains a typo.',
      },
    ],
  },
  {
    id: 'notifications',
    title: 'Notifications',
    steps: [
      {
        title: 'What the Notifications page shows',
        body: 'Three live views derived from your order data:\n\n• Overdue Orders — past planned shipping date, not yet shipped\n• Overdue Payments — invoice due date passed, not yet paid\n• Upcoming Shipments — shipping within a configurable window',
      },
      {
        title: 'Adjusting the upcoming-shipments window',
        body: 'Use the {notification-days|days range control} to set how many days ahead to look for upcoming shipments. Enter a number and click Confirm to update the table. The default is 7 days.',
      },
    ],
  },
  {
    id: 'files-templates',
    title: 'Files & Templates',
    steps: [
      {
        title: 'The Files page',
        body: 'Every generated document is archived here. Switch between the {files-pdf-tab|PDF tab} and the {files-xls-tab|XLS tab} to browse by type. Each entry shows the filename, associated order ID, and a download button.',
      },
      {
        title: 'Template list',
        body: 'The {files-template-list|template list} shows all document templates currently active. Templates control the layout and content of generated PDFs. Click a template entry to download the current version.',
      },
      {
        title: 'Uploading a new template',
        body: 'Click {files-new-template|Upload new template} and select a .docx or .xlsx file. The filename must match the expected naming convention exactly — uploads with the wrong name are rejected by the server. Check the template list to see the exact filename required for each slot.',
      },
    ],
  },
  {
    id: 'activity-log',
    title: 'Activity Log',
    steps: [
      {
        title: 'What the log records',
        body: 'Every import, document generation, and status change creates a log entry with a timestamp, user, action type, status (success / warning / fail), and a summary. The log is the first place to check when something goes wrong.',
      },
      {
        title: 'Searching logs',
        body: 'Use the {log-search|search bar} to find entries by order ID, customer name, error message, or any other text. Toggle {log-show-tests|Show tests} to include or exclude automated test runs.',
      },
      {
        title: 'Expanding a log entry',
        body: 'Click the {log-expand|expand arrow} on any row to see full details: a summary of affected orders, warnings in amber, errors in red, and a Replay JSON block with the exact payload used (useful for debugging failed imports by identifying which rows were rejected).',
      },
    ],
  },

  // ── FAQ ────────────────────────────────────────────────────────────────────
  {
    id: 'faq',
    title: 'FAQ & Common Issues',
    steps: [
      {
        title: 'Why are the document buttons greyed out?',
        body: 'Buttons in the {action-panel|action panel} are only enabled when at least one order is selected. Check the {order-select|checkbox column} — if no rows are ticked, all generate buttons are disabled.\n\nAlso make sure the selected order is in the correct status for that document type (e.g. {stwxe-button|STWXE} requires status "fresh"; {invoice-button|Invoice} requires "stwxe").',
      },
      {
        title: 'What are the red-bordered fields with *?',
        body: 'Fields outlined in red with an asterisk (*) are mandatory for that document type. The dialog will not submit until every required field has a value. Scroll through the entire dialog — required fields may appear below the visible area.',
        figure: MandatoryFieldsFigure,
      },
      {
        title: 'Import failed: "required fields missing"',
        body: 'One of the four mandatory columns is absent or misspelled in your spreadsheet. Column names are case-sensitive. Open the Activity Log and expand the failed entry — it will name the exact column that was missing.\n\nCompare your spreadsheet headers against the required set:',
        figure: ImportFormatFigure,
      },
      {
        title: '"Consignment fully consumed" error',
        body: 'The master consignment order\'s partial_delivered_qty has reached the total_qty. No further invoices can be generated against it.\n\nTo close out, click {mark-paid-button|Mark Paid} on the master order. If the total quantity was set incorrectly, use {edit-button|Edit} to correct it first.',
      },
      {
        title: 'Invoice generated but no XML appeared',
        body: 'XRechnung XML is only generated when the customer\'s nationality is set to DE. Open the order via {edit-button|Edit} and check the customer nationality field. Update it and re-generate the invoice — the re-generation creates a new entry in Files without removing the old one.',
      },
      {
        title: 'Spotlight shows a hint box instead of highlighting',
        body: 'The target element is not in the DOM yet. This happens with elements inside dialogs (like the {generate-submit|Generate button|Open the Generate dialog first, then click this reference again}): the dialog must be open before the element exists on the page.\n\nOpen the relevant dialog first, then click the blue reference link again.',
      },
      {
        title: 'Status didn\'t change after generating',
        body: 'Open the {nav-log|Activity Log} and look for a fail or warning entry near the time of generation. The log will contain the server-side error message.\n\nCommon causes: a required field was blank in the database (not just the dialog), a template file was missing from the server, or a network timeout interrupted the request.',
      },
      {
        title: 'Can\'t find a specific order',
        body: 'The order is most likely hidden by an active filter. Click {reset-button|Reset} to clear the {search-form|search bar} and all {boomer-buttons|quick-filter buttons} at once.\n\nAlso check whether the order was cancelled — cancelled orders are hidden from the default view. Select the "cancelled" quick-filter to surface them.',
      },
      {
        title: 'Template upload was rejected',
        body: 'Template filenames must follow an exact naming convention — the server checks the filename and rejects anything that doesn\'t match a known template slot. Open the {files-template-list|template list} to see the exact filename expected for each slot, then rename your file to match before uploading.',
      },
      {
        title: 'STWXE Checker shows "Not in DB"',
        body: 'The invoice ID was not found in the orders database. Possible causes:\n\n• The order was never imported — check the {nav-log|Activity Log} for a past import containing that ID\n• The ID has a typo — compare with the physical PDF carefully (0 vs O, 1 vs l are common mistakes)\n• The order was imported under a different order_ID value than the invoice number on the document',
      },
    ],
  },
];
